'use client';

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  createEbook,
  acceptEbookTerms,
  uploadEbookSourceFile,
  uploadEbookPreviewFile,
  uploadEbookCoverFile,
} from '@/utils/api';
import { EBOOK_CATEGORIES } from '@/constants/ebookCategories';

export default function CreateEbookPage() {
  const router = useRouter();

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('other');
  const [description, setDescription] = useState('');
  const [pricePi, setPricePi] = useState('0');
  const [priceUsd, setPriceUsd] = useState(""); // optional
const [language, setLanguage] = useState("en");

    const TERMS_VERSION = "2025-12-16"; // keep in sync with backend constant
  const TERMS_LS_KEY = "ebookTermsAcceptedVersion";

  const [showTerms, setShowTerms] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  // Authors (real book authors, not uploader)
  const [authorsInput, setAuthorsInput] = useState('');
  const authors = useMemo(
    () => authorsInput.split(',').map((s) => s.trim()).filter(Boolean),
    [authorsInput]
  );

  // Tags
  const [tagsInput, setTagsInput] = useState('');
  const tags = useMemo(
    () => tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
    [tagsInput]
  );

  // Cover (uploaded)
  const [coverUrl, setCoverUrl] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);

  // Source ebook file (epub/pdf)
  const [ebookFileUrl, setEbookFileUrl] = useState('');
  const [ebookFileKey, setEbookFileKey] = useState('');
  const [ebookUploading, setEbookUploading] = useState(false);
  const [ebookProgress, setEbookProgress] = useState(0);
  const [ebookBytes, setEbookBytes] = useState(0);
  const [ebookMime, setEbookMime] = useState('');
  const [ebookOriginalName, setEbookOriginalName] = useState('');

  // Preview (pdf)
  const [previewFileKey, setPreviewFileKey] = useState('');
  const [previewUploading, setPreviewUploading] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [previewBytes, setPreviewBytes] = useState(0);
  const [previewMime, setPreviewMime] = useState('');
  const [previewOriginalName, setPreviewOriginalName] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');

    const doCreate = async () => {
    await createEbook({
      title,
      description,
      pricePi: Number(pricePi),
      priceUsd: priceUsd === "" ? null : Number(priceUsd),
      coverUrl: coverUrl || "",
      category,
      tags,
      authors,
language, // ✅ ADD THIS
      source: {
        key: ebookFileKey,
        cdnUrl: ebookFileUrl || "",
        bytes: ebookBytes,
        contentType: ebookMime || "",
      },

      preview: previewFileKey
        ? {
            file: {
              key: previewFileKey,
              bytes: previewBytes,
              contentType: previewMime || "application/pdf",
            },
            imageUrls: [],
          }
        : undefined,
    });

    setOkMsg("Ebook created (pending review).");
    router.push("/creator/ebooks/mine");
  };

  async function onPickPreviewFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setErr('');
    setPreviewUploading(true);
    setPreviewProgress(0);
    setPreviewOriginalName(file.name);

    try {
      const up = await uploadEbookPreviewFile({
        file,
        draftId: 'ebook-draft',
        onProgress: (p) => setPreviewProgress(p),
      });

      setPreviewFileKey(up.key);
      setPreviewMime(up.contentType || file.type || 'application/pdf');
      setPreviewBytes(up.bytes || file.size || 0);
    } catch (e2) {
      setErr(e2?.message || 'Preview upload failed');
    } finally {
      setPreviewUploading(false);
    }
  }

  async function onPickEbookFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const ok =
      file.type === 'application/pdf' ||
      file.type === 'application/epub+zip' ||
      file.name.toLowerCase().endsWith('.pdf') ||
      file.name.toLowerCase().endsWith('.epub');

    if (!ok) {
      setErr('Please upload an EPUB (.epub) or PDF (.pdf).');
      e.target.value = '';
      return;
    }

    setErr('');
    setEbookUploading(true);
    setEbookProgress(0);
    setEbookOriginalName(file.name);

    try {
      const up = await uploadEbookSourceFile({
        file,
        draftId: 'ebook-draft',
        onProgress: (p) => setEbookProgress(p),
      });

      setEbookFileUrl(up.cdnUrl);
      setEbookFileKey(up.key);

      const fallbackMime = file.name.toLowerCase().endsWith('.pdf')
        ? 'application/pdf'
        : 'application/epub+zip';

      setEbookMime(up.contentType || file.type || fallbackMime);
      setEbookBytes(up.bytes || file.size || 0);
    } catch (e2) {
      setErr(e2?.message || 'Ebook file upload failed');
    } finally {
      setEbookUploading(false);
    }
  }

  async function onPickCover(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setErr('');
    setCoverUploading(true);
    setCoverProgress(0);

    try {
      const up = await uploadEbookCoverFile({
        file,
        draftId: 'ebook-draft',
        onProgress: (p) => setCoverProgress(p),
      });

      setCoverUrl(up.cdnUrl);
    } catch (e2) {
      setErr(e2?.message || 'Cover upload failed');
    } finally {
      setCoverUploading(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setOkMsg('');

    if (ebookUploading) {
      setErr('Wait for the ebook upload to finish.');
      return;
    }
    if (previewUploading) {
      setErr('Wait for the preview upload to finish (or remove it).');
      return;
    }
    if (!ebookFileKey) {
      setErr('Upload an ebook file (EPUB or PDF).');
      return;
    }
if (!category) {
  setErr("Pick a category.");
  return;
}

       try {
      setLoading(true);

      // Soft gate (UX): show modal only once per terms version
      const acceptedV =
        typeof window !== "undefined" ? localStorage.getItem(TERMS_LS_KEY) : null;

      if (acceptedV !== TERMS_VERSION) {
        setPendingSubmit(true);
        setTermsChecked(false);
        setShowTerms(true);
        return;
      }

      await doCreate();
    } catch (e2) {
      // Hard gate (security): backend says terms not accepted
      if (e2?.data?.code === "TERMS_NOT_ACCEPTED") {
        setPendingSubmit(true);
        setTermsChecked(false);
        setShowTerms(true);
        return;
      }

      setErr(e2?.message || "Failed to create ebook");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:px-6 lg:px-8 text-textPrimary">
      <Link href="/ebooks" className="inline-flex items-center text-sm mb-4 hover:underline">
        &larr; Back to ebooks
      </Link>

      <h1 className="text-2xl font-semibold mb-4">Create Ebook</h1>

      {err && (
        <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm">
          {err}
        </div>
      )}

      {okMsg && (
        <div className="mb-4 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm">
          {okMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:border-gray-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:border-gray-400"
          >
            <option value="" disabled className="bg-black">
    Select a category
  </option>
           {EBOOK_CATEGORIES.map((c) => (
  <option key={c.key} value={c.key} className="bg-black">
    {c.label}
  </option>
))}

          </select>
        </div>
<div>
  <label className="block text-sm font-medium mb-1">Language</label>
  <select
    value={language}
    onChange={(e) => setLanguage(e.target.value)}
    className="w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:border-gray-400"
  >
    <option value="en" className="bg-black">English</option>
    <option value="es" className="bg-black">Spanish</option>
    <option value="fr" className="bg-black">French</option>
    <option value="de" className="bg-black">German</option>
    <option value="it" className="bg-black">Italian</option>
    <option value="pt" className="bg-black">Portuguese</option>
    <option value="el" className="bg-black">Greek</option>
    <option value="la" className="bg-black">Latin</option>
    <option value="ar" className="bg-black">Arabic</option>
  </select>

  <div className="mt-1 text-xs opacity-70">
    Use a 2-letter code (like <code>en</code>, <code>es</code>).
  </div>
</div>

        <div>
          <label className="block text-sm font-medium mb-1">Book author(s)</label>
          <input
            type="text"
            value={authorsInput}
            onChange={(e) => setAuthorsInput(e.target.value)}
            placeholder="Jane Austen, Mark Twain"
            className="w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:border-gray-400"
          />
          <div className="mt-1 text-xs opacity-70">
            These are the real authors on the book (not the uploader).
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Short description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:border-gray-400 resize-y"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Price (in π)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={pricePi}
            onChange={(e) => setPricePi(e.target.value)}
            required
            className="w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:border-gray-400"
          />
        </div>
<div>
  <label className="block text-sm font-medium mb-1">
    Price (in USDC) <span className="text-xs opacity-70">(optional, for web checkout)</span>
  </label>
  <input
    type="number"
    min="0"
    step="0.01"
    value={priceUsd}
    onChange={(e) => setPriceUsd(e.target.value)}
    className="w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:border-gray-400"
  />
  <div className="mt-1 text-xs opacity-70">
    If empty, the ebook can still be sold in Pi Browser, but USDC checkout won’t appear.
  </div>
</div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Ebook file (EPUB recommended, PDF optional)
          </label>
          <input
            type="file"
            accept=".epub,.pdf,application/epub+zip,application/pdf"
            onChange={onPickEbookFile}
          />
          <div className="mt-1 text-xs opacity-70">
            EPUB works best on all devices. PDF is fixed-layout and may not preview in Pi Browser.
          </div>

          {ebookUploading && (
            <div className="mt-2 text-xs opacity-80">Uploading… {ebookProgress}%</div>
          )}

          {ebookFileKey && (
            <div className="mt-2 text-xs opacity-80">
              Uploaded ✅ {ebookOriginalName || 'ebook'} ({Math.round((ebookBytes || 0) / 1024)} KB)
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Preview PDF (free sample)</label>
          <input type="file" accept=".pdf,application/pdf" onChange={onPickPreviewFile} />

          {previewUploading && (
            <div className="mt-2 text-xs opacity-80">Uploading… {previewProgress}%</div>
          )}

          {previewFileKey && (
            <div className="mt-2 text-xs opacity-80">
              Uploaded ✅ {previewOriginalName || 'preview.pdf'} ({Math.round((previewBytes || 0) / 1024)} KB)
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Cover image</label>
          <input
            type="file"
            accept="image/*"
            onChange={onPickCover}
            className="block w-full text-sm"
          />

          {coverUploading && (
            <div className="mt-2 text-xs opacity-80">Uploading… {coverProgress}%</div>
          )}

          {coverUrl && (
            <div className="mt-2 flex items-start gap-3">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={coverUrl}
                alt="Cover preview"
                className="h-24 w-24 rounded-md object-cover border border-gray-700"
              />
              <button
                type="button"
                onClick={() => setCoverUrl('')}
                className="text-xs underline opacity-80 hover:opacity-100"
              >
                Remove
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="crypto, web3, tutorial"
            className="w-full rounded-md border border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:border-gray-400"
          />
        </div>

        <button
          type="submit"
          disabled={loading || coverUploading || ebookUploading || previewUploading}
          className="mt-2 inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-textPrimary disabled:opacity-60"
        >
          {loading ? 'Saving…' : 'Create ebook'}
        </button>
      </form>
  {/* ✅ Modal OUTSIDE form but INSIDE the page wrapper */}
    {showTerms && (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => {
            setShowTerms(false);
            setPendingSubmit(false);
            setTermsChecked(false);
          }}
        />

        <div className="relative w-full max-w-lg rounded-2xl bg-background p-6 border border-border">
          <h2 className="text-lg font-semibold mb-2">Before you upload</h2>

          <p className="text-sm text-textSecondary mb-4">
            You must confirm you have the rights to upload and distribute this eBook (including cover images).
          </p>

          <label className="flex gap-3 text-sm mb-5">
            <input
              type="checkbox"
              checked={termsChecked}
              onChange={(e) => setTermsChecked(e.target.checked)}
              className="mt-1"
            />
            <span>
              I confirm that I own or have permission to upload and distribute this content,
              and I agree to the{" "}
              <Link href="/terms" className="underline hover:opacity-80">
                Terms of Service
              </Link>
              .
            </span>
          </label>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowTerms(false);
                setPendingSubmit(false);
                setTermsChecked(false);
              }}
              className="px-4 py-2 rounded-xl border border-border"
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={!termsChecked}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white disabled:opacity-50"
              onClick={async () => {
                try {
                  setErr("");
                  setLoading(true);

                  await acceptEbookTerms();

                  if (typeof window !== "undefined") {
                    localStorage.setItem(TERMS_LS_KEY, TERMS_VERSION);
                  }

                  setShowTerms(false);

                  if (pendingSubmit) {
                    setPendingSubmit(false);
                    await doCreate();
                  }
                } catch (e3) {
                  setErr(e3?.message || "Failed to accept terms");
                } finally {
                  setLoading(false);
                }
              }}
            >
              I Agree & Continue
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);}